<script setup>
import {defineProps, onMounted, ref} from "vue";

defineProps({
  navigationRoot: {
    type: Object,
    required: true,
  }
});

const panels = ref([]);

function generatePanelsFromUrl(url) {
  const segments = url.split('/').filter(segment => segment);
  const panelsArray = [];
  let currentPath = '';
  segments.forEach(segment => {
    currentPath += (currentPath ? '/' : '') + segment;
    panelsArray.push(currentPath);
  });
  return panelsArray;
}

function getPageUrl(page) {
  return page?.externalUrl || ('/' + page?.url);
}

function isExternalUrl(page) {
  return !!page?.externalUrl;
}

onMounted(() => {
  const currentUrl = window.location.pathname.substring(1);
  panels.value = generatePanelsFromUrl(currentUrl);
});
</script>

<template>
  <nav class="simple-page-side-navigation">
    <a v-if="isExternalUrl(navigationRoot)" :href="getPageUrl(navigationRoot)" target="_blank" class="navigation-root no-children">
      {{ navigationRoot?.urlText }}
    </a>
    <router-link v-else :to="getPageUrl(navigationRoot)" class="navigation-root no-children">
      {{ navigationRoot?.urlText }}
    </router-link>

    <v-expansion-panels v-if="navigationRoot?.children?.length" v-model="panels" multiple>
      <template v-for="(firstLevelPage, key) in navigationRoot?.children" :key="key">
        <v-expansion-panel v-if="firstLevelPage?.children && firstLevelPage?.children?.length > 0" elevation="0"
                           :value="firstLevelPage?.url">
          <v-expansion-panel-title>
            <span class="not-a-router">
              {{ firstLevelPage?.urlText }}
            </span>
            <template v-slot:actions="{ expanded }">
              <v-icon :icon="expanded ? 'mdi-minus' : 'mdi-plus'"></v-icon>
            </template>
          </v-expansion-panel-title>
          <v-expansion-panel-text>
            <v-expansion-panels multiple>
              <template v-for="(secondLevelPage, key) in firstLevelPage?.children" :key="key">
                <v-expansion-panel v-if="secondLevelPage?.children && secondLevelPage?.children?.length > 0"
                                   elevation="0"
                                   :value="secondLevelPage?.url">
                  <v-expansion-panel-title>
                    <a v-if="isExternalUrl(secondLevelPage)" :href="getPageUrl(secondLevelPage)" target="_blank" class="second-level-page">
                      {{ secondLevelPage?.urlText }}
                    </a>
                    <router-link v-else :to="getPageUrl(secondLevelPage)" class="second-level-page">
                      {{ secondLevelPage?.urlText }}
                    </router-link>
                    <template v-slot:actions="{ expanded }">
                      <v-icon :icon="expanded ? 'mdi-minus' : 'mdi-plus'"></v-icon>
                    </template>
                  </v-expansion-panel-title>
                  <v-expansion-panel-text>
                    <template v-for="(thirdLevelPage, key) in secondLevelPage?.children" :key="key">
                      <a v-if="isExternalUrl(thirdLevelPage)" :href="getPageUrl(thirdLevelPage)" target="_blank" class="third-level-page no-children">
                        {{ thirdLevelPage?.urlText }}
                      </a>
                      <router-link v-else :to="getPageUrl(thirdLevelPage)" class="third-level-page no-children">
                        {{ thirdLevelPage?.urlText }}
                      </router-link>
                    </template>
                  </v-expansion-panel-text>
                </v-expansion-panel>
                <a v-else-if="isExternalUrl(secondLevelPage)" :href="getPageUrl(secondLevelPage)" target="_blank" class="second-level-page no-children">
                  {{ secondLevelPage?.urlText }}
                </a>
                <router-link v-else :to="getPageUrl(secondLevelPage)" class="second-level-page no-children">
                  {{ secondLevelPage?.urlText }}
                </router-link>
              </template>
            </v-expansion-panels>
          </v-expansion-panel-text>
        </v-expansion-panel>
        <a v-else-if="isExternalUrl(firstLevelPage)" :href="getPageUrl(firstLevelPage)" target="_blank" class="no-children">
          {{ firstLevelPage?.urlText }}
        </a>
        <router-link v-else :to="getPageUrl(firstLevelPage)" class="no-children">
          {{ firstLevelPage?.urlText }}
        </router-link>
      </template>
    </v-expansion-panels>
  </nav>
</template>

<style scoped lang="scss">
.headline-container {
  margin: 1rem 0 !important;
}

:deep(.v-expansion-panel-text__wrapper) {
  padding: 0;
}

:deep(.v-expansion-panel-title__icon) {
  width: 4rem;
  justify-content: center;
}

:deep(.v-expansion-panel) {
  &::after {
    content: none;
  }
}

a, .not-a-router {
  display: block;
  width: 100%;
  padding: .5rem 1rem;
  z-index: 9;
  text-decoration: none;
  font-size: .875rem;

  &.second-level-page {
    padding-left: 2rem;
  }

  &.third-level-page {
    padding-left: 3rem;
  }
}

</style>
